/**
 * HTTP transport with retries and exponential backoff (PRD §34).
 *
 * Used by any real data provider. Deliberately small: retry the errors
 * that are worth retrying, give up quickly on the ones that aren't, and
 * never retry so aggressively that a rate limit turns into a ban.
 */

export class HttpError extends Error {
  constructor(status, statusText, url) {
    super(`Request failed with ${status} ${statusText}`);
    this.name = "HttpError";
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.userMessage = "We're having trouble getting the latest play.";
  }
}

/** 408 timeout, 429 rate limit, and anything 5xx are worth another go. */
export function isRetryableStatus(status) {
  return status === 408 || status === 429 || (status >= 500 && status < 600);
}

/** Full jitter backoff, capped, so N clients don't retry in lockstep. */
export function backoffDelay(attempt, { baseMs = 500, maxMs = 8000, jitter = Math.random } = {}) {
  const exponential = Math.min(maxMs, baseMs * 2 ** attempt);
  return Math.round(exponential * (0.5 + jitter() * 0.5));
}

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * @param {string} url
 * @param {{
 *   retries?: number,
 *   timeoutMs?: number,
 *   signal?: AbortSignal,
 *   headers?: object,
 *   fetchImpl?: typeof fetch,
 *   sleepImpl?: (ms: number) => Promise<void>,
 *   jitter?: () => number
 * }} [options]
 */
export async function fetchJson(url, options = {}) {
  const {
    retries = 2,
    timeoutMs = 8000,
    signal,
    headers,
    fetchImpl = typeof fetch === "function" ? fetch : null,
    sleepImpl = sleep,
    jitter,
  } = options;

  if (!fetchImpl) {
    throw new Error("No fetch implementation available in this environment.");
  }

  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller =
      typeof AbortController === "function" ? new AbortController() : null;

    const timer =
      controller && timeoutMs
        ? setTimeout(() => controller.abort(), timeoutMs)
        : null;

    // Honour a caller-supplied signal alongside our own timeout.
    const onExternalAbort = () => controller?.abort();
    signal?.addEventListener?.("abort", onExternalAbort);

    try {
      const response = await fetchImpl(url, {
        headers,
        signal: controller?.signal ?? signal,
      });

      if (!response.ok) {
        const error = new HttpError(response.status, response.statusText, url);
        if (!isRetryableStatus(response.status) || attempt === retries) {
          throw error;
        }
        lastError = error;
      } else {
        return await response.json();
      }
    } catch (caught) {
      // A deliberate cancellation is not a failure worth retrying.
      if (caught?.name === "AbortError" && signal?.aborted) throw caught;

      // A 404 or a 401 is not going to fix itself; don't burn the budget.
      if (caught instanceof HttpError && !isRetryableStatus(caught.status)) {
        throw caught;
      }

      if (attempt === retries) throw caught;
      lastError = caught;
    } finally {
      if (timer) clearTimeout(timer);
      signal?.removeEventListener?.("abort", onExternalAbort);
    }

    await sleepImpl(backoffDelay(attempt, { jitter }));
  }

  throw lastError ?? new Error("Request failed");
}
